/**
 * PesticideSyncStatusPanel — admin import-panel widget showing catalog sync
 * status (edition, last sync, counts) + a prominent stale alert (AK-385 addendum).
 *
 * Shown in the platform-admin pesticide import panel. Unlike the user plaque,
 * this probes the portal (checkLatest=true) so the operator sees «доступна
 * свежая редакция» and knows to run an update.
 *
 * DS: Card + Alert (variant warning) + Badge, all @/components/ui/*, semantic
 * tokens only. No local colors.
 */
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatRuDate } from '@/lib/dateFmt';
import { usePesticideSyncStatus } from '@/lib/pesticides/syncStatus';

interface Props {
  className?: string;
}

export function PesticideSyncStatusPanel({ className }: Props) {
  const { data, isLoading, isError, refetch, isFetching } =
    usePesticideSyncStatus({ checkLatest: true });

  if (isLoading) {
    return (
      <Card className={className}>
        <div className="space-y-2 p-4">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card className={className}>
        <div className="p-4 text-sm text-muted-foreground">
          Не удалось загрузить статус синхронизации Госкаталога.
        </div>
      </Card>
    );
  }

  const neverSynced = !data.sourceEdition;

  return (
    <Card className={className} data-testid="pesticide-sync-panel">
      <div className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-foreground">
            Синхронизация Госкаталога Минсельхоза
          </h3>
          <button
            type="button"
            onClick={() => void refetch()}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
            disabled={isFetching}
            title="Проверить статус"
          >
            <RefreshCw
              className={isFetching ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'}
              aria-hidden
            />
            Обновить статус
          </button>
        </div>

        {neverSynced ? (
          <p className="text-sm text-muted-foreground">
            Каталог ещё не синхронизирован. Запустите импорт, чтобы загрузить
            реестр пестицидов.
          </p>
        ) : (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <dt className="text-muted-foreground">Редакция данных</dt>
            <dd className="text-foreground" data-testid="sync-edition">
              {formatRuDate(data.sourceEdition)}
            </dd>

            <dt className="text-muted-foreground">Синхронизировано</dt>
            <dd className="text-foreground">
              {data.syncedAt ? formatRuDate(data.syncedAt) : '—'}
            </dd>

            <dt className="text-muted-foreground">Препаратов</dt>
            <dd className="text-foreground">{data.counts.products ?? '—'}</dd>

            <dt className="text-muted-foreground">Регламентов</dt>
            <dd className="text-foreground">{data.counts.regulations ?? '—'}</dd>

            <dt className="text-muted-foreground">В очереди на разбор</dt>
            <dd className="text-foreground">
              {data.counts.queuePending != null &&
              data.counts.queuePending > 0 ? (
                <Badge variant="secondary">{data.counts.queuePending}</Badge>
              ) : (
                (data.counts.queuePending ?? '—')
              )}
            </dd>
          </dl>
        )}

        {data.isStale && data.latestAvailableEdition ? (
          <Alert variant="warning" data-testid="sync-stale-alert">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            <AlertTitle>Доступна свежая редакция</AlertTitle>
            <AlertDescription>
              На портале Минсельхоза опубликована редакция от{' '}
              {formatRuDate(data.latestAvailableEdition)}. Запустите обновление,
              чтобы синхронизировать каталог.
            </AlertDescription>
          </Alert>
        ) : null}
      </div>
    </Card>
  );
}
